import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Paper, Stack, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import ComandoLivreDialog from './ComandoLivreDialog';
import ListaComandosDialog from './ListaComandosDialog';
import PoolComandoDialog from './PoolComandoDialog';
import { COR_CATEGORIA, SUPERFICIE, getItem } from './estilo';

const ROTA = '/admin/snapshot';
const VENDORS_CORE = ['HUAWEI', 'NOKIA', 'CISCO', 'JUNIPER'];

/** Nível 1 tem fact (logo, parser e diff estruturado). Nível 2 não tem. */
function nivelDe(cmd) {
  return cmd.fact ? 1 : 2;
}

function CelulaVazia({ children }) {
  return <Typography variant="caption" sx={{ color: '#94a3b8' }}>{children}</Typography>;
}

// =====================================================================
// Aba: Comandos
// =====================================================================

function AbaComandos({ pools, empresa, onEditar, onNovo, carregando }) {
  const [filtroVendor, setFiltroVendor] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busca, setBusca] = useState('');

  const categorias = useMemo(
    () => [...new Set(pools.map((p) => p.category).filter(Boolean))].sort(),
    [pools]
  );

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return pools.filter((p) => {
      if (filtroVendor && !p.vendors.some((v) => v.vendor === filtroVendor)) return false;
      if (filtroNivel && String(p.level) !== filtroNivel) return false;
      if (filtroCategoria && p.category !== filtroCategoria) return false;
      if (termo) {
        const alvo = `${p.label} ${p.fact_key || ''} ${p.vendors.map((v) => v.command).join(' ')}`;
        if (!alvo.toLowerCase().includes(termo)) return false;
      }
      return true;
    });
  }, [pools, filtroVendor, filtroNivel, filtroCategoria, busca]);

  return (
    <>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          size="small" label="Buscar comando ou conceito" sx={{ width: 280 }}
          value={busca} onChange={(e) => setBusca(e.target.value)}
        />
        <TextField
          select size="small" label="Vendor" sx={{ width: 160 }}
          value={filtroVendor} onChange={(e) => setFiltroVendor(e.target.value)}
        >
          <MenuItem value="">Todos</MenuItem>
          {VENDORS_CORE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Categoria" sx={{ width: 150 }}
          value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}
        >
          <MenuItem value="">Todas</MenuItem>
          {categorias.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Nível" sx={{ width: 200 }}
          value={filtroNivel} onChange={(e) => setFiltroNivel(e.target.value)}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="1">1 — com parser</MenuItem>
          <MenuItem value="2">2 — livre</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={onNovo}>
          Comando livre
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Cada linha é um <strong>conceito</strong>, com as grafias de cada equipamento
        juntas — “versão do sistema” é <code>display version</code> no Huawei e
        <code> show version</code> no Cisco. Editar mexe nas duas de uma vez;
        separadas, uma é corrigida e a outra fica para trás.
      </Alert>

      <TableContainer component={Paper} variant="outlined" sx={SUPERFICIE}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>Conceito</TableCell>
              <TableCell>Grafias</TableCell>
              <TableCell sx={{ width: 70 }}>Nível</TableCell>
              <TableCell align="right" sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.map((p) => (
              <TableRow key={p.key} hover>
                <TableCell>
                  <Stack spacing={0.4}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small" label={p.category}
                        sx={{
                          height: 19, fontSize: 10.5, color: '#fff',
                          bgcolor: COR_CATEGORIA[p.category] || COR_CATEGORIA.OUTROS,
                        }}
                      />
                      <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{p.label}</Typography>
                    </Stack>
                    {p.fact_key && (
                      <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                        {p.fact_key}
                      </Typography>
                    )}
                    {!!p.missing_vendors.length && (
                      <Tooltip title="Estes equipamentos não têm grafia cadastrada: o comando simplesmente não roda neles.">
                        <Typography variant="caption" sx={{ color: '#d97706' }}>
                          falta {p.missing_vendors.join(', ')}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack spacing={0.25}>
                    {p.vendors.map((v) => (
                      <Stack key={v.id} direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ color: '#475569', minWidth: 62 }}>
                          {v.vendor}
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                          {v.command}
                        </Typography>
                        {v.is_heavy && (
                          <Tooltip title="Saída grande ou demorada; roda com timeout maior.">
                            <BoltIcon sx={{ fontSize: 14, color: '#d97706' }} />
                          </Tooltip>
                        )}
                        {!v.enabled && (
                          <Chip size="small" label="desativado"
                            sx={{ height: 16, fontSize: 10, bgcolor: '#94a3b8', color: '#fff' }} />
                        )}
                        {!v.has_parser && p.partial_parser && (
                          <Tooltip title="Coleta o texto, mas fica de fora do diff estruturado.">
                            <Typography variant="caption" sx={{ color: '#d97706' }}>
                              sem parser
                            </Typography>
                          </Tooltip>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Tooltip title={p.level === 1
                    ? 'Todas as grafias têm parser: a saída vira estrutura e entra na comparação.'
                    : 'Ao menos uma grafia não tem parser: o diff dela fica no texto bruto.'}
                  >
                    <Chip
                      size="small" label={String(p.level)}
                      sx={{
                        height: 20, width: 28,
                        bgcolor: p.level === 1 ? '#2563eb' : '#7f8c8d', color: '#fff',
                      }}
                    />
                  </Tooltip>
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="Editar o conceito e todas as grafias">
                    <IconButton size="small" onClick={() => onEditar({ ...p, empresa })}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!lista.length && !carregando && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {pools.length ? 'Nenhum conceito com esses filtros.' : 'Nenhum comando cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// =====================================================================
// Aba: Listas
// =====================================================================

function AbaListas({ empresa, onRecarregar, listas }) {
  const [dialogo, setDialogo] = useState(false);
  const [aberta, setAberta] = useState(null);
  const [detalhe, setDetalhe] = useState({});
  const [carregandoDet, setCarregandoDet] = useState(null);

  // O conteudo de cada lista so e buscado quando alguem abre. Carregar todas de
  // uma vez seria uma consulta por lista para dado que quase nunca e olhado.
  const abrir = async (id) => {
    if (aberta === id) { setAberta(null); return; }
    setAberta(id);
    if (detalhe[id]) return;
    setCarregandoDet(id);
    try {
      const res = await api.get(`${ROTA}/command-set/${id}/detail`, { params: { empresa } });
      setDetalhe((d) => ({ ...d, [id]: res.data?.data || null }));
    } catch {
      setDetalhe((d) => ({ ...d, [id]: null }));
    } finally {
      setCarregandoDet(null);
    }
  };

  return (
    <>
      <Stack direction="row" sx={{ mb: 2 }} alignItems="center">
        <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
          Uma lista agrupa os comandos que rodam num snapshot. A coleta guarda
          qual lista usou, então as coletas seguintes da mesma sessão executam
          exatamente o mesmo conjunto — é isso que torna a comparação honesta.
        </Alert>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo(true)}>
          Nova lista
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={SUPERFICIE}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Padrão</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listas.map((l) => (
              <React.Fragment key={l.id}>
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => abrir(l.id)}>
                  <TableCell sx={{ py: 0.25 }}>
                    <IconButton size="small">
                      {aberta === l.id
                        ? <ExpandLessIcon sx={{ fontSize: 18 }} />
                        : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PlaylistAddCheckIcon sx={{ fontSize: 17, color: '#475569' }} />
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{l.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {l.description
                      ? <Typography variant="caption">{l.description}</Typography>
                      : <CelulaVazia>—</CelulaVazia>}
                  </TableCell>
                  <TableCell align="right">
                    {l.is_default
                      ? <Chip size="small" label="padrão" sx={{ height: 20, bgcolor: '#2563eb', color: '#fff' }} />
                      : <CelulaVazia>—</CelulaVazia>}
                  </TableCell>
                </TableRow>

                {aberta === l.id && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ bgcolor: '#f8fafc', py: 1 }}>
                      {carregandoDet === l.id && <CircularProgress size={15} />}

                      {detalhe[l.id] && !detalhe[l.id].command_count && (
                        <Alert severity="warning" sx={{ py: 0 }}>
                          Lista sem nenhum comando. Uma coleta com ela termina
                          “concluída” sem coletar nada.
                        </Alert>
                      )}

                      {!!detalhe[l.id]?.command_count && (
                        <Stack spacing={0.4}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {detalhe[l.id].command_count} comando(s)
                          </Typography>
                          {detalhe[l.id].commands.map((c) => (
                            <Stack key={c.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small" label={c.category}
                                sx={{
                                  height: 17, fontSize: 10, minWidth: 62,
                                  bgcolor: COR_CATEGORIA[c.category] || COR_CATEGORIA.OUTROS,
                                  color: '#fff',
                                }}
                              />
                              <Typography variant="caption" sx={{ color: '#475569', minWidth: 64 }}>
                                {c.vendor}
                              </Typography>
                              <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                                {c.command}
                              </Typography>
                              {c.label && (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  — {c.label}
                                </Typography>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}

            {!listas.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Nenhuma lista cadastrada.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ListaComandosDialog
        aberto={dialogo}
        empresa={empresa}
        onFechar={() => setDialogo(false)}
        onCriado={onRecarregar}
      />
    </>
  );
}


function AbaFacts({ facts }) {
  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Um fact define <strong>como comparar</strong>. A regra que faz o Snapshot Watcher
        funcionar está aqui: a <strong>identidade</strong> nunca inclui o lugar. Por isso
        um vizinho que troca de interface aparece como <em>migrou</em>, e não como sumiu + apareceu.
      </Alert>

      <TableContainer component={Paper} variant="outlined" sx={SUPERFICIE}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fact</TableCell>
              <TableCell>Identidade</TableCell>
              <TableCell>Lugar</TableCell>
              <TableCell>Ignorado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facts.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small" label={f.category}
                      sx={{
                        height: 20, color: '#fff',
                        bgcolor: COR_CATEGORIA[f.category] || COR_CATEGORIA.OUTROS,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{f.name}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{f.key}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(f.identity_keys || []).map((k) => (
                      <Chip key={k} size="small" label={k}
                        sx={{ height: 19, fontFamily: 'monospace', bgcolor: '#e0e7ff', color: '#1e3a8a' }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  {(f.location_keys || []).length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {f.location_keys.map((k) => (
                        <Chip key={k} size="small" label={k}
                          sx={{ height: 19, fontFamily: 'monospace', bgcolor: '#ede9fe', color: '#5b21b6' }} />
                      ))}
                    </Stack>
                  ) : <CelulaVazia>—</CelulaVazia>}
                </TableCell>
                <TableCell>
                  {(f.volatile_keys || []).length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {f.volatile_keys.map((k) => (
                        <Tooltip key={k} title="Muda sozinho entre coletas; não vira evento.">
                          <Chip size="small" label={k}
                            sx={{ height: 19, fontFamily: 'monospace', bgcolor: '#f1f5f9', color: '#475569' }} />
                        </Tooltip>
                      ))}
                    </Stack>
                  ) : <CelulaVazia>—</CelulaVazia>}
                </TableCell>
              </TableRow>
            ))}
            {!facts.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Nenhum fact cadastrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// =====================================================================

export default function CatalogoComandos() {
  const notify = useNotify();

  // O catálogo é GLOBAL. Fact e comando são conhecimento de vendor, não de
  // cliente: `display ospf peer brief` num Huawei devolve a mesma coisa em
  // qualquer provedor. A empresa só entra no cadastro de comando livre (nível 2),
  // que é do parque de quem cadastrou.
  const empresa = getItem(localStorage, 'hermes')?.empresa || null;
  const [aba, setAba] = useState(0);
  const [carregando, setCarregando] = useState(false);

  const [pools, setPools] = useState([]);
  const [listas, setListas] = useState([]);
  const [facts, setFacts] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const [dialogoLivre, setDialogoLivre] = useState(false);
  const [aEditar, setAEditar] = useState(null);

  useEffect(() => {
    document.title = 'IPV7 Hermes - Catálogo de Comandos';
  }, []);

  // Categorias da mesma fonte que o cadastro de comando livre usa — duas listas
  // paralelas divergiriam no primeiro acréscimo.
  useEffect(() => {
    api.get(`${ROTA}/command/options`)
      .then((res) => setCategorias(res.data?.data?.categories || []))
      .catch(() => { });
  }, []);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setCarregando(true);
    try {
      const [c, l, f] = await Promise.all([
        api.get(`${ROTA}/command/pools`, { params: { empresa } }),
        api.post(`${ROTA}/command-set/`, { limit: 200, offset: 0 }),
        api.post(`${ROTA}/fact/`, { limit: 200, offset: 0 }),
      ]);
      setPools(c.data?.data || []);
      setListas(l.data?.data || []);
      setFacts(f.data?.data || []);
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao carregar o catálogo' });
    } finally {
      setCarregando(false);
    }
    // notify vem de um Context estável; incluí-lo causaria recarga extra.
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#0f172a' }}>Catálogo de Comandos</Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label="catálogo compartilhado"
          sx={{ bgcolor: '#e0e7ff', color: '#1e3a8a' }}
        />
        <Tooltip title="Recarregar">
          <span>
            <IconButton onClick={carregar} disabled={carregando} sx={{ color: '#475569' }}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {(
        <Paper variant="outlined" sx={{ p: 2, ...SUPERFICIE }}>
          <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ mb: 2 }}>
            <Tab label={`Comandos (${pools.length})`} />
            <Tab label={`Listas (${listas.length})`} />
            <Tab label={`Facts (${facts.length})`} />
          </Tabs>
          <Divider sx={{ mb: 2 }} />

          {carregando && <CircularProgress size={20} sx={{ mb: 2 }} />}

          {aba === 0 && (
            <AbaComandos
              pools={pools} empresa={empresa} carregando={carregando}
              onEditar={setAEditar}
              onNovo={() => setDialogoLivre(true)}
            />
          )}
          {aba === 1 && (
            <AbaListas listas={listas} empresa={empresa} onRecarregar={carregar} />
          )}
          {aba === 2 && <AbaFacts facts={facts} />}
        </Paper>
      )}

      <ComandoLivreDialog
        aberto={dialogoLivre}
        empresa={empresa}
        onFechar={() => setDialogoLivre(false)}
        onCriado={carregar}
      />

      <PoolComandoDialog
        pool={aEditar}
        categorias={categorias}
        onFechar={() => setAEditar(null)}
        onSalvo={carregar}
      />
    </Box>
  );
}
